import type {
  DayView,
  NoncashRowKind,
  ReconcileMark,
  ReviewCounts,
  ReviewQueue,
  ShiftDetail,
  ShiftListResult,
  ShiftReviewStatus,
} from '@didto/domain';
import {
  RECONCILE_MARKS,
  SHIFT_REVIEW_STATUS_LABELS,
  amrOf,
  authRecent,
  describeOverShort,
  markLabel,
} from '@didto/domain';
import type { Tone } from '@didto/ui';
import { useQuery } from '@tanstack/react-query';
import type { ShiftSummary } from './shifts';
import { supabase } from './supabase';
import { decodeJwtPayload } from './jwt';

/**
 * The owner's review (Phase 7A-2A, ADR-026). Five read RPCs, each refused unless the caller is a
 * signed-in human holding shift.review at the location (never a tablet PIN session); every write is
 * a command. The pages read what the server computed: review items with their digests, the day's
 * figures, the non-cash rows with their marks. Nothing here decides anything.
 */
export type ShiftDetailView = Omit<ShiftDetail, 'frozen' | 'live'> & {
  frozen: ShiftSummary | null;
  live: ShiftSummary;
};

export const reviewKeys = {
  counts: (locationId: string) => ['review-counts', locationId] as const,
  queue: (locationId: string) => ['review-queue', locationId] as const,
  shifts: (locationId: string, from: string, to: string, needsReview: boolean) =>
    ['review-shifts', locationId, from, to, needsReview] as const,
  shift: (shiftId: string) => ['review-shift', shiftId] as const,
  day: (locationId: string, date: string | null) => ['review-day', locationId, date ?? ''] as const,
  // the money reports read the same ledger: an owner's correction refreshes them too (ADR-043)
  all: [
    'review-counts',
    'review-queue',
    'review-shifts',
    'review-shift',
    'review-day',
    'report-money',
  ],
};

/** Every review query: invalidated after any review or reconciliation command. */
export const REVIEW_INVALIDATE: string[][] = reviewKeys.all.map((k) => [k]);

export async function fetchReviewCounts(locationId: string): Promise<ReviewCounts> {
  const { data, error } = await supabase.rpc('review_counts', { p_location_id: locationId });
  if (error) throw error;
  return data as unknown as ReviewCounts;
}

export async function fetchReviewQueue(locationId: string): Promise<ReviewQueue> {
  const { data, error } = await supabase.rpc('review_queue', { p_location_id: locationId });
  if (error) throw error;
  return data as unknown as ReviewQueue;
}

export async function fetchShiftList(
  locationId: string,
  from: string,
  to: string,
  needsReview: boolean,
): Promise<ShiftListResult> {
  const { data, error } = await supabase.rpc('shift_list', {
    p_location_id: locationId,
    p_from: from,
    p_to: to,
    p_needs_review: needsReview,
  });
  if (error) throw error;
  return data as unknown as ShiftListResult;
}

export async function fetchShiftDetail(shiftId: string): Promise<ShiftDetailView> {
  const { data, error } = await supabase.rpc('shift_detail', { p_shift_id: shiftId });
  if (error) throw error;
  return data as unknown as ShiftDetailView;
}

export async function fetchDayView(locationId: string, date: string | null): Promise<DayView> {
  const { data, error } = await supabase.rpc('day_view', {
    p_location_id: locationId,
    ...(date ? { p_business_date: date } : {}),
  });
  if (error) throw error;
  return data as unknown as DayView;
}

export function useReviewCounts(locationId: string | null, enabled = true) {
  return useQuery({
    queryKey: reviewKeys.counts(locationId ?? ''),
    enabled: enabled && !!locationId,
    queryFn: () => fetchReviewCounts(locationId!),
    refetchInterval: 60_000,
    staleTime: 15_000,
  });
}

/* ── the token's age ── */
/** The claims of the current access token (the `amr` entries carry when the user last authenticated). */
export async function currentClaims(): Promise<Record<string, unknown> | null> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) return null;
  return decodeJwtPayload(token);
}

/** Mirrors app.auth_recent on the token in hand: whether a sensitive action would be accepted now. */
export async function tokenIsFresh(withinSeconds: number): Promise<boolean> {
  const claims = await currentClaims();
  return authRecent(amrOf(claims), withinSeconds);
}

/* ── labels ── */
export const ACK_STATE_LABEL: Record<
  string,
  { label: string; tone: 'danger' | 'warning' | 'info' | 'success' }
> = {
  open: { label: 'To check', tone: 'danger' },
  changed: { label: 'Changed since', tone: 'warning' },
  follow_up: { label: 'Follow-up', tone: 'info' },
  not_mine: { label: 'Not mine', tone: 'danger' },
  closed: { label: 'Cleared', tone: 'success' },
};

/** A shift's or a day's review status; a shift still open is work in progress, not yet to review. */
export const REVIEW_STATUS_TONE: Record<string, Tone> = {
  to_check: 'danger',
  not_reviewed: 'warning',
  changed: 'warning',
  reviewed: 'success',
  nothing: 'neutral',
  open: 'info',
};

export function reviewStatusLabel(status: string): string {
  if (status === 'nothing') return 'No shifts';
  if (status === 'open') return 'Open';
  return SHIFT_REVIEW_STATUS_LABELS[status as ShiftReviewStatus] ?? status;
}

/** What an approval asks for (the owner's phone, an item's facts), in words. */
export const ACTION_TITLE: Record<string, string> = {
  'order.send': 'Send before payment',
  'order.void_line': 'Void a line',
  'order.void': 'Void an order',
  'payment.void': 'Void a payment',
  'order.refund': 'Refund',
  'ticket.unserve': 'Take back a ticket',
  'order.unserve': 'Take back an order',
  'shift.takeover_start': 'Take over a shift',
  'shift.close_finish': 'Close a shift',
  'order.write_off': 'Write off',
  'order.return_line': 'Return an item',
  'table.force_clear': 'Clear a table by force',
};

/** An item's title as the pages show it: the server's, except an approval's, whose action reads in words. */
export function itemTitle(item: {
  kind: string;
  title: string;
  facts: Record<string, unknown>;
}): string {
  const action = item.facts.action;
  if (item.kind === 'pin.approval' && typeof action === 'string' && ACTION_TITLE[action])
    return `Approved: ${ACTION_TITLE[action]}`;
  return item.title;
}

export const CLOSE_KIND_LABEL: Record<string, string> = {
  cashier: 'Closed by the cashier',
  owner: 'Closed by the owner',
  handover: 'Handed over',
  takeover: 'Taken over',
};

/** A business date as the pages show it. */
export function fmtBusinessDate(date: string, locale = 'en-PH'): string {
  const d = new Date(`${date}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return date;
  return d.toLocaleDateString(locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
}

export function addDays(date: string, days: number): string {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/* ── an item's facts, in words ── */
export interface FactFormats {
  money: (minor: number) => string;
  at: (iso: string) => string;
  date: (date: string) => string;
}

/** What each fact is called, in the order the owner reads them: money, counts, what happened, when. A fact
 * not listed reads as its own name in words, after these. */
const FACT_LABEL: Record<string, string> = {
  amount_minor: 'Amount',
  total_minor: 'Total',
  over_short_minor: 'Over / short',
  frozen_over_short_minor: 'Over / short at the close',
  opening_difference_minor: 'Opening difference',
  frozen_opening_difference_minor: 'Opening difference as recorded',
  counted_minor: 'Counted',
  expected_minor: 'Expected',
  opening_count_minor: 'Opening count',
  expected_opening_minor: 'Expected at the opening',
  tolerance_minor: 'Tolerance',
  settled_minor: 'Settled',
  unsettled_minor: 'Still unsettled',
  observed_amount_minor: "In the provider's record",
  received_minor: 'Cash received',
  removed_minor: 'Taken out',
  corrected_minor: 'As corrected',
  paid_minor: 'Paid',
  refunded_minor: 'Given back',
  written_off_minor: 'Written off',
  walkout_minor: 'Walk-out',
  reversed_minor: 'Refunded or voided first',
  cash_back_minor: 'Cash given back',
  short_minor: 'Short over the 7 days',
  order_given_minor: 'Given back on the order',
  order_max_minor: 'Limit per order',
  day_given_minor: 'Given back that day',
  day_max_minor: 'Limit per day',
  limit_minor: 'Limit per order',
  count: 'How many',
  cash_back_count: 'Times cash was given back',
  qty: 'Quantity',
  attempts: 'Count attempts',
  earlier_uses: 'Earlier uses of the reference',
  no_receipt: 'Without a receipt',
  seconds_apart: 'Time apart',
  method: 'Method',
  kind: 'Kind',
  row_kind: 'Row',
  mark: 'Checked as',
  action: 'Action',
  authority: 'On whose authority',
  source: 'Reference',
  reason: 'Reason',
  close_kind: 'Close',
  open_kind: 'Opening',
  status: 'Shift',
  fulfilment_status: 'Kitchen and bar',
  end_reason: 'Ended',
  original_kind: 'Was',
  entity_type: 'On',
  stale: 'Past its business date',
  long_open: 'Open too long',
  self: 'For the person who recorded it',
  voided: 'Voided',
  cash_for_noncash: 'Cash given back for a non-cash sale',
  outside_shift: 'Outside a shift',
  format_mismatch: 'Not in the expected format',
  over_threshold: 'Over the limit per pay-out',
  cashier_day_over: "Over the cashier's day limit",
  payee_day_over: "Over the payee's day limit",
  after_cancelled_count: 'After a cancelled count',
  business_date: 'Business date',
  from: 'From',
  opened_at: 'Opened',
  started_at: 'Started',
  placed_at: 'Placed',
  sent_at: 'Sent',
  voided_at: 'Voided',
  ended_at: 'Ended',
  closed_at: 'Closed',
  marked_at: 'Checked',
  granted_at: 'Approved',
  recorded_at: 'Recorded',
};
const FACT_ORDER = Object.keys(FACT_LABEL);

/** Differences read as over, short or balanced. */
const DIFFERENCE_FACTS = new Set([
  'over_short_minor',
  'frozen_over_short_minor',
  'opening_difference_minor',
  'frozen_opening_difference_minor',
]);

const ROW_KIND_WORDS: Record<string, string> = {
  payment: 'Payment',
  void: 'Void',
  refund: 'Refund',
  pair: 'Paid, then voided',
};
const SOURCE_WORDS: Record<string, string> = {
  counter: 'Confirmed at the counter',
  queued: 'Sent late, not confirmed at the counter',
  owner_entry: 'Recorded by the owner',
};
const AUTHORITY_WORDS: Record<string, string> = {
  cashier: 'The cashier alone',
  owner: 'The owner',
};

const capitalised = (v: string) => v.charAt(0).toUpperCase() + v.slice(1);
/** A code (`owner_close`, `order.void`) in words; anything else as it is. */
const words = (v: string) =>
  /^[a-z][a-z_.]*$/.test(v) ? capitalised(v.replace(/[_.]+/g, ' ')) : v;

function factLabel(key: string): string {
  return FACT_LABEL[key] ?? capitalised(words(key.replace(/_minor$/, '').replace(/_at$/, '')));
}

function factValue(
  key: string,
  v: string | number | boolean,
  facts: Record<string, unknown>,
  fmt: FactFormats,
): string {
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'number') {
    if (key.endsWith('_minor'))
      return DIFFERENCE_FACTS.has(key) ? describeOverShort(v, fmt.money) : fmt.money(v);
    if (key === 'seconds_apart') {
      const s = Math.round(v);
      return s < 90 ? `${s} s` : `${Math.round(s / 60)} min`;
    }
    return String(v);
  }
  if (key.endsWith('_at')) return Number.isNaN(Date.parse(v)) ? v : fmt.at(v);
  if (key === 'business_date' || key === 'from') return fmt.date(v);
  const rowKind = facts.row_kind;
  switch (key) {
    // what someone wrote, and a method's own name, read as they are
    case 'reason':
    case 'method':
      return v;
    case 'action':
      return ACTION_TITLE[v] ?? words(v);
    case 'close_kind':
      return CLOSE_KIND_LABEL[v] ?? words(v);
    case 'row_kind':
      return ROW_KIND_WORDS[v] ?? words(v);
    case 'source':
      return SOURCE_WORDS[v] ?? words(v);
    case 'authority':
      return AUTHORITY_WORDS[v] ?? words(v);
    case 'mark':
      return (RECONCILE_MARKS as readonly string[]).includes(v) &&
        typeof rowKind === 'string' &&
        rowKind in ROW_KIND_WORDS
        ? markLabel(rowKind as NoncashRowKind, v as ReconcileMark)
        : words(v);
    default:
      return words(v);
  }
}

/** The facts an item is made of, without the ids, as the owner reads them under the title: in words, money in
 * the business's currency, times in its time zone, the money first. */
export function readableFacts(
  facts: Record<string, unknown>,
  fmt: FactFormats,
): Array<[string, string]> {
  const rank = (key: string, i: number) => {
    const at = FACT_ORDER.indexOf(key);
    return at >= 0 ? at : FACT_ORDER.length + i;
  };
  return Object.entries(facts)
    .flatMap(([k, v], i) =>
      !/(^|_)ids?$/.test(k) &&
      (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean')
        ? [{ k, v, r: rank(k, i) }]
        : [],
    )
    .sort((a, b) => a.r - b.r)
    .map(({ k, v }): [string, string] => [factLabel(k), factValue(k, v, facts, fmt)]);
}
